import type { AxiosInstance } from 'axios';
import {
  EventEditResponse, EventEditType, EventPriceTier, EventLink, Organizer, OrganizerEditResponse,
  EventImageProposal, OrganizerDuplicateGroup, OrganizerMember, OrganizerAssetProposal, UserLookupResult,
  PageResponse, DecideOrganizerAccessRequest, MergeOrganizersRequest, DismissOrganizerDuplicatesRequest,
  ReviewOrganizerEditRequest, DuplicateGroup, CyclingEvent, OrganizerKind, eventCategories,
} from '../types';
import { CATEGORY_LABELS, EVENT_TYPE_LABELS, LINK_TYPE_LABELS, displayBundesland } from '../brand';
import { formatDayShort, parseDisplayDate } from '../dateFormat';
import { distanceShortLabel, formatRegistrationStart, canonicalLinkUrl } from '../utils';
import { sortPriceTiers, priceTierUntilLabel } from '../priceTiers';
import { seasonLabel } from '../permanentSeason';
import { EventFormWerte, bereinigteLinks, leereStrecke } from './inhalte';
import type { ReviewEditOverrides } from '../types';

/**
 * Abrufe der Verwaltung, Bereich Prüfen: Vorschläge, Duplikate, Veranstalter-Zugänge.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Event-Vorschläge selbst (Liste, Detail, Prüfen, Ähnliche, Favoriten zählen) und Event-Duplikate
 * stehen schon in `createEventService` und werden von dort benutzt, nicht doppelt geführt.
 */
export function createPruefenService(api: AxiosInstance) {
  return {
    // ── Veranstalter-Vorschläge ──
    veranstalterVorschlaege: async (page = 0, size = 20): Promise<PageResponse<OrganizerEditResponse>> =>
      (await api.get('/organizer-edits/pending', { params: { page, size } })).data,
    veranstalterVorschlag: async (id: number): Promise<OrganizerEditResponse> =>
      (await api.get(`/organizer-edits/${id}`)).data,
    veranstalterVorschlagPruefen: async (id: number, data: ReviewOrganizerEditRequest): Promise<OrganizerEditResponse> =>
      (await api.post(`/organizer-edits/${id}/review`, data)).data,
    alleVeranstalterVorschlaegeAnnehmen: async (): Promise<{ approved: number }> =>
      (await api.post('/organizer-edits/approve-all')).data,

    // ── Bild-Vorschläge der Enricher ──
    bildVorschlaege: async (): Promise<EventImageProposal[]> =>
      (await api.get('/event-image-proposals', { params: { status: 'PENDING' } })).data,
    bildVorschlagPruefen: async (id: number, approve: boolean, focusX = 50, focusY = 50): Promise<void> => {
      await api.post(`/event-image-proposals/${id}/review`, { approve, focusX, focusY });
    },

    // ── Veranstalter-Duplikate ──
    veranstalterDuplikate: async (): Promise<OrganizerDuplicateGroup[]> =>
      (await api.get('/organizers/duplicates')).data,
    veranstalterZusammenfuehren: async (data: MergeOrganizersRequest): Promise<void> => {
      await api.post('/organizers/merge', data);
    },
    veranstalterKeinDuplikat: async (data: DismissOrganizerDuplicatesRequest): Promise<void> => {
      await api.post('/organizers/duplicates/dismiss', data);
    },

    // ── Veranstalter-Zugänge ──
    zugangsAntraege: async (page = 0, size = 50): Promise<PageResponse<OrganizerMember>> =>
      (await api.get('/organizer-access/pending', { params: { page, size } })).data,
    zugangEntscheiden: async (id: number, data: DecideOrganizerAccessRequest): Promise<OrganizerMember> =>
      (await api.post(`/organizer-access/${id}/decide`, data)).data,
    zugangErteilen: async (organizerId: number, userId: number): Promise<OrganizerMember> =>
      (await api.post('/organizer-access/grant', { organizerId, userId })).data,
    zugangEntziehen: async (id: number): Promise<void> => {
      await api.delete(`/organizer-access/${id}`);
    },
    zugaengeVon: async (organizerId: number): Promise<OrganizerMember[]> =>
      (await api.get(`/organizer-access/by-organizer/${organizerId}`)).data,
    ohnePruefung: async (organizerId: number, enabled: boolean): Promise<Organizer> =>
      (await api.post(`/organizer-access/organizers/${organizerId}/auto-approve`, { enabled })).data,
    kontenSuchen: async (q: string): Promise<UserLookupResult[]> =>
      (await api.get('/organizer-access/user-lookup', { params: { q } })).data,
    bilderInPruefung: async (): Promise<OrganizerAssetProposal[]> =>
      (await api.get('/organizer-access/asset-proposals')).data,
    bildEntscheiden: async (id: number, approved: boolean, note?: string): Promise<void> => {
      await api.post(`/organizer-access/asset-proposals/${id}/review`, { approved, note });
    },

    // ── Veranstalter (Redaktionssicht mit Kontaktdaten) ──
    veranstalterListe: async (search?: string): Promise<Organizer[]> =>
      (await api.get('/organizers/admin', { params: search ? { search } : {} })).data,
    veranstalterVoll: async (id: number): Promise<Organizer> =>
      (await api.get(`/organizers/${id}/full`)).data,
    /** Verein oder kommerziell: Voraussetzung für Zugang und Rechnung (OrganizerAccessService). */
    einordnen: async (id: number, orgKind: OrganizerKind): Promise<Organizer> =>
      (await api.put(`/organizers/${id}/kind`, { orgKind })).data,

    /**
     * Ein geschütztes Bild (Vorschlagsfoto, Bild-Vorschlag, Logo in Prüfung) als data-URL. Ein
     * `<Image source={{ uri }}>` schickt den Token nicht mit, also holt Axios die Bytes. Kein
     * FileReader/btoa: beides gibt es nicht überall, die Kodierung hier läuft in Browser und App.
     */
    geschuetztesBild: async (pfad: string): Promise<string> => {
      const res = await api.get(pfad, { responseType: 'arraybuffer' });
      const typ = String((res.headers as Record<string, unknown>)['content-type'] || 'image/jpeg').split(';')[0];
      return `data:${typ};base64,${base64(new Uint8Array(res.data as ArrayBuffer))}`;
    },
  };
}

export type PruefenService = ReturnType<typeof createPruefenService>;

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function base64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i], b = bytes[i + 1], c = bytes[i + 2];
    out += B64[a >> 2] + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]
      + (b === undefined ? '=' : B64[((b & 15) << 2) | ((c ?? 0) >> 6)])
      + (c === undefined ? '=' : B64[c & 63]);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Veranstalter-Zugänge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hat der Verein den Antrag schon bestätigen können? Beleg für die Prüfung eines Zugangsantrags
 * (Web `OrganizerAccess.tsx`, App `belege.tsx`). Ein geklickter Link macht den Antrag sofort zum
 * Zugang — was hier noch steht, hat der Verein also nicht bestätigt. Läuft der Link noch, kann
 * die Redaktion abwarten; ist er abgelaufen, hat der Verein vier Tage lang nicht reagiert.
 */
export function freischaltLinkBeleg(
  m: OrganizerMember, jetzt: Date = new Date(),
): { ton: 'plain' | 'warn'; text: string } {
  if (!m.approvalLinkSentTo) return { ton: 'plain', text: 'Keine Freischalt-Mail an den Verein verschickt' };
  const an = `Freischalt-Mail an ${m.approvalLinkSentTo} am ${formatDayShort(m.approvalLinkSentAt)}`;
  const ablauf = parseDisplayDate(m.approvalLinkExpiresAt);
  return ablauf && ablauf.getTime() < jetzt.getTime()
    ? { ton: 'warn', text: `${an}, nicht bestätigt und abgelaufen` }
    : { ton: 'plain', text: `${an}, noch nicht bestätigt (gültig bis ${formatDayShort(m.approvalLinkExpiresAt)})` };
}

// ─────────────────────────────────────────────────────────────────────────────
// Was ein Event-Vorschlag ändert — dieselben Regeln wie die Prüfansicht im Web (ReviewEdits.tsx).
// ─────────────────────────────────────────────────────────────────────────────

/** Preisstufen in einer Zeile: „Frühbucher bis 31.12.2026 | bis 31.03.2027". */
export const preisstufenText = (tiers?: EventPriceTier[] | null): string =>
  sortPriceTiers(tiers).map(t => [t.label, priceTierUntilLabel(t)].filter(Boolean).join(' ')).join(' | ');

type AnmeldungFelder = { registrationState?: string | null; registrationMode?: string | null; registrationStart?: string | null };

/** Anmeldung in Worten: Stand, sonst Art, sonst Start. */
export const anmeldungText = (r?: AnmeldungFelder | null): string => {
  if (!r) return '';
  if (r.registrationState === 'OPEN') return 'bereits geöffnet';
  if (r.registrationState === 'CLOSED') return 'geschlossen';
  if (r.registrationMode === 'ONSITE_ONLY') return 'nur vor Ort';
  return r.registrationStart ? `ab ${formatRegistrationStart(r.registrationStart)}` : '';
};

/** Ändert der Vorschlag die Anmeldung? Wie EventEditService: nur Genanntes zählt. */
export const anmeldungGeaendert = (edit: EventEditResponse): boolean => {
  const orig = edit.originalEvent as (CyclingEvent & AnmeldungFelder) | undefined;
  if (!orig) return false;
  if (edit.registrationState && edit.registrationState !== 'UNKNOWN' && edit.registrationState !== (orig.registrationState ?? 'UNKNOWN')) return true;
  if (edit.registrationMode && edit.registrationMode !== 'UNKNOWN' && edit.registrationMode !== (orig.registrationMode ?? 'UNKNOWN')) return true;
  return !!edit.registrationStart && edit.registrationStart !== (orig.registrationStart ?? null);
};

/** Ändert der Vorschlag die Preisstufen? `null` heißt „nicht übermittelt". */
export const preisstufenGeaendert = (edit: EventEditResponse): boolean =>
  edit.priceTiers != null && preisstufenText(edit.priceTiers) !== preisstufenText(edit.originalEvent?.priceTiers);

/** Eine Zeile im Vergleich: Feld, alter Wert, neuer Wert. */
export interface Aenderung {
  feld: string;
  label: string;
  vorher: string;
  nachher: string;
  geaendert: boolean;
  /** Lang und mehrzeilig (Beschreibung, Belege): eingeklappt zeigen. */
  lang?: boolean;
}

const leer = (v: unknown): string => (v == null ? '' : String(v).trim());
const janein = (v: unknown): string => (v ? 'Ja' : 'Nein');
const strecken = (ds?: { distanceKm: number; elevationMeters?: number | null; durationMinutes?: number | null; startTime?: string | null; eventType?: string | null }[]): string =>
  (ds ?? []).map(d => [
    distanceShortLabel(d as never),
    d.elevationMeters ? `${d.elevationMeters} hm` : '',
    d.startTime ? `Start ${d.startTime}` : '',
    d.eventType ? EVENT_TYPE_LABELS[d.eventType] ?? d.eventType : '',
  ].filter(Boolean).join(' · ')).join('\n');
const linksText = (ls?: EventLink[]): string =>
  (ls ?? []).map(l => `${LINK_TYPE_LABELS[l.type] ?? l.type}: ${l.url}`).join('\n');
const kategorien = (e: { category: string; secondaryCategories?: string[] | null }): string =>
  eventCategories(e as never).map(c => CATEGORY_LABELS[c] ?? c).join(', ');

/**
 * Veranstalter verknüpfungsgesteuert vergleichen: bleibt die Verknüpfung und trägt der Vorschlag
 * keinen Freitext, ändert sich nichts (sonst stünde dort ein Phantom „Veranstalter gelöscht").
 */
export const veranstalterGeaendert = (edit: EventEditResponse): boolean => {
  const orig = edit.originalEvent;
  if (!orig) return false;
  if ((orig.organizerId ?? null) !== (edit.organizerId ?? null)) return true;
  const vorgeschlagen = edit.organizer ?? '';
  if (vorgeschlagen === '' && orig.organizerId != null) return false;
  return vorgeschlagen !== (orig.organizer ?? '');
};

/** URLs, die der Vorschlag neu ans Event bringt (Links werden nur ergänzt, nie ersetzt). */
export const neueLinks = (edit: EventEditResponse): EventLink[] => {
  if (edit.type === EventEditType.CREATE) return edit.links ?? [];
  const vorhanden = new Set((edit.originalEvent?.links ?? []).map(l => canonicalLinkUrl(l.url)));
  return (edit.links ?? []).filter(l => !vorhanden.has(canonicalLinkUrl(l.url)));
};

const streckenGeaendert = (edit: EventEditResponse): boolean => {
  const orig = edit.originalEvent?.distances;
  if (!orig) return false;
  const neu = edit.distances;
  if (orig.length !== neu.length) return true;
  return neu.some((d, i) =>
    d.distanceKm !== orig[i].distanceKm
    || d.elevationMeters !== orig[i].elevationMeters
    || d.routeUrl !== orig[i].routeUrl
    || (d.durationMinutes ?? null) !== (orig[i].durationMinutes ?? null)
    || (d.eventType != null && d.eventType !== (orig[i].eventType ?? null))
    || (d.startTime != null && d.startTime !== (orig[i].startTime ?? null)));
};

/**
 * Alle Felder eines Vorschlags im Vergleich mit dem Event. Bei einem neuen Event gibt es kein
 * Vorher; dann steht jedes ausgefüllte Feld als „geändert" drin. Felder, die ein Vorschlag nicht
 * trägt (`null`), lassen den Wert am Event stehen und zählen nicht als Änderung — dieselben
 * Rückfälle wie `initOverrides` im Web.
 */
export function vorschlagVergleich(edit: EventEditResponse): Aenderung[] {
  const o = edit.originalEvent as (CyclingEvent & AnmeldungFelder & { participants?: number; endDate?: string; startTimeEnd?: string }) | undefined;
  const neu = edit.type === EventEditType.CREATE || !o;
  const z = (feld: string, label: string, nachher: string, vorher: string, geaendert?: boolean, lang?: boolean): Aenderung => ({
    feld, label, nachher, vorher: neu ? '' : vorher,
    geaendert: geaendert ?? (neu ? nachher !== '' : nachher !== vorher), lang,
  });
  const permanentNeu = edit.permanent ?? o?.permanent ?? false;
  const saisonQuelle = edit.permanent != null ? edit : (o ?? {});
  const zeit = (s?: string | null, e?: string | null) => (s ? `${s} Uhr${e ? ` bis ${e} Uhr` : ''}` : '');
  const ort = (e?: { street?: string; houseNumber?: string; postalCode?: string; city?: string }) =>
    [[e?.street, e?.houseNumber].filter(Boolean).join(' '), [e?.postalCode, e?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const ende = edit.endDate ?? o?.endDate;
  const startEnde = edit.startTimeEnd ?? o?.startTimeEnd;
  const teilnehmer = edit.participants ?? o?.participants;
  const flyer = edit.flyerUrl ?? o?.flyerUrlExternal;
  const links = neueLinks(edit);
  const veranstalterNeu = edit.organizer
    || (edit.organizerId != null ? o?.organizer : undefined)
    || (edit.newOrganizerName ? `${edit.newOrganizerName} (neu)` : '')
    || '';

  return [
    z('title', 'Titel', leer(edit.title), leer(o?.title)),
    z('eventDate', 'Datum', formatDayShort(edit.eventDate, ''), formatDayShort(o?.eventDate, '')),
    z('endDate', 'Enddatum', ende ? formatDayShort(ende) : '', o?.endDate ? formatDayShort(o.endDate) : ''),
    z('startTime', 'Startzeit', zeit(edit.startTime, startEnde), zeit(o?.startTime, o?.startTimeEnd)),
    z('locationName', 'Treffpunkt', leer(edit.locationName), leer(o?.locationName)),
    z('adresse', 'Adresse', ort(edit), ort(o)),
    z('bundesland', '(Bundes)Land', displayBundesland(edit.bundesland) ?? '', displayBundesland(o?.bundesland) ?? ''),
    z('category', 'Kategorie', kategorien(edit), o ? kategorien(o) : ''),
    z('eventType', 'Eventart', leer(EVENT_TYPE_LABELS[edit.eventType ?? o?.eventType ?? ''] ?? ''), leer(EVENT_TYPE_LABELS[o?.eventType ?? ''] ?? '')),
    z('organizer', 'Veranstalter', veranstalterNeu, leer(o?.organizer), neu ? veranstalterNeu !== '' : veranstalterGeaendert(edit) || !!edit.newOrganizerName),
    z('organizerUrl', 'Veranstaltungs-Website', leer(edit.organizerUrl), leer(o?.organizerUrl)),
    z('registration', 'Anmeldung', anmeldungText(edit), anmeldungText(o), neu ? anmeldungText(edit) !== '' : anmeldungGeaendert(edit)),
    z('priceTiers', 'Preisstufen', edit.priceTiers == null ? preisstufenText(o?.priceTiers) : preisstufenText(edit.priceTiers),
      preisstufenText(o?.priceTiers), neu ? preisstufenText(edit.priceTiers) !== '' : preisstufenGeaendert(edit)),
    z('participants', 'Teilnehmer der letzten Auflage', leer(teilnehmer), leer(o?.participants)),
    z('fullyBooked', 'Ausgebucht', janein(edit.fullyBooked), janein(o?.fullyBooked), neu ? !!edit.fullyBooked : !!edit.fullyBooked !== !!o?.fullyBooked),
    z('cancelled', 'Abgesagt', janein(edit.cancelled), janein(o?.cancelled), neu ? !!edit.cancelled : !!edit.cancelled !== !!o?.cancelled),
    z('tentative', 'Vorläufig', janein(edit.tentative), janein(o?.tentative), neu ? !!edit.tentative : !!edit.tentative !== !!o?.tentative),
    z('permanent', 'Jederzeit fahrbar',
      permanentNeu ? `Ja, ${seasonLabel(saisonQuelle)}${(edit.permanent != null ? edit.areaName : o?.areaName) ? `, ${edit.permanent != null ? edit.areaName : o?.areaName}` : ''}` : 'Nein',
      o?.permanent ? `Ja, ${seasonLabel(o)}${o.areaName ? `, ${o.areaName}` : ''}` : 'Nein',
      neu ? !!permanentNeu : undefined),
    z('flyerUrl', 'Flyer-Link', leer(flyer), leer(o?.flyerUrlExternal)),
    z('distances', 'Strecken', strecken(edit.distances), strecken(o?.distances), neu ? edit.distances.length > 0 : streckenGeaendert(edit)),
    z('links', neu ? 'Links' : 'Neue Links', linksText(links), neu ? '' : linksText(o?.links), links.length > 0),
    z('description', 'Beschreibung', leer(edit.description), leer(o?.description), undefined, true),
  ];
}

/** Zeile der Vorschlagsliste samt Stellung in einer Gruppe zum selben Event (wie im Web). */
export interface VorschlagZeile {
  edit: EventEditResponse;
  gruppe: number;
  indexInGruppe: number;
}

/** Vorschläge zum selben Event untereinander, der älteste zuerst; sonst bleibt die Reihenfolge. */
export function vorschlaegeGruppieren(edits: EventEditResponse[]): VorschlagZeile[] {
  const jeEvent: Record<number, EventEditResponse[]> = {};
  edits.forEach(e => {
    if (e.type !== EventEditType.EDIT || !e.eventId) return;
    (jeEvent[e.eventId] = jeEvent[e.eventId] ?? []).push(e);
  });
  const gesetzt: Record<number, true> = {};
  const zeilen: VorschlagZeile[] = [];
  edits.forEach(e => {
    if (gesetzt[e.id]) return;
    const gruppe = (e.type === EventEditType.EDIT && e.eventId && jeEvent[e.eventId]) || [e];
    const sortiert = gruppe.length > 1 ? [...gruppe].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')) : gruppe;
    sortiert.forEach((m, i) => { gesetzt[m.id] = true; zeilen.push({ edit: m, gruppe: sortiert.length, indexInGruppe: i }); });
  });
  return zeilen;
}

/** Vorauswahl „mit erledigen": andere Änderungen zum selben Event, keine neuen Events. */
export const mitErledigenVorauswahl = (edit: EventEditResponse): number[] =>
  (edit.siblings?.mergeCandidates ?? []).filter(c => c.type === EventEditType.EDIT).map(c => c.id);

/** Wer den Vorschlag eingereicht hat, in einer Zeile. */
export const einreicherText = (edit: { submittedByUsername?: string; submitterName?: string; submitterEmail?: string }): string =>
  edit.submittedByUsername ?? edit.submitterName ?? edit.submitterEmail ?? 'Anonym';


// ─────────────────────────────────────────────────────────────────────────────
// Veranstalter-Vorschlag: Feld für Feld (fieldDiff im Web)
// ─────────────────────────────────────────────────────────────────────────────

/** Leere Strings gelten als „kein Vorschlag" — Agenten schicken teils "". */
export function feldVergleich(vorgeschlagen?: string | null, aktuell?: string | null): { wert?: string; vorher?: string; geaendert: boolean } {
  const p = vorgeschlagen?.trim() || undefined;
  const c = aktuell?.trim() || undefined;
  if (!p || p === c) return { wert: c ?? p, geaendert: false };
  return { wert: p, vorher: c, geaendert: true };
}

export const VERANSTALTER_VORSCHLAG_FELDER: { label: string; vorschlag: keyof OrganizerEditResponse; aktuell: keyof Organizer; kontakt?: boolean; region?: boolean }[] = [
  { label: 'Name', vorschlag: 'proposedName', aktuell: 'name' },
  { label: 'Website', vorschlag: 'proposedWebsite', aktuell: 'website' },
  { label: 'Straße', vorschlag: 'proposedStreet', aktuell: 'street' },
  { label: 'PLZ', vorschlag: 'proposedPostalCode', aktuell: 'postalCode' },
  { label: 'Ort', vorschlag: 'proposedCity', aktuell: 'city' },
  { label: '(Bundes)Land', vorschlag: 'proposedBundesland', aktuell: 'bundesland', region: true },
  { label: 'Impressum', vorschlag: 'proposedImpressumUrl', aktuell: 'impressumUrl' },
  { label: 'Strava (Club)', vorschlag: 'proposedStravaClubUrl', aktuell: 'stravaClubUrl' },
  { label: 'Instagram', vorschlag: 'proposedInstagramUrl', aktuell: 'instagramUrl' },
  { label: 'Facebook', vorschlag: 'proposedFacebookUrl', aktuell: 'facebookUrl' },
  { label: 'Komoot', vorschlag: 'proposedKomootUrl', aktuell: 'komootUrl' },
  { label: 'Ansprechpartner', vorschlag: 'proposedContactPerson', aktuell: 'contactPerson', kontakt: true },
  { label: 'E-Mail', vorschlag: 'proposedEmail', aktuell: 'email', kontakt: true },
  { label: 'Telefon', vorschlag: 'proposedPhone', aktuell: 'phone', kontakt: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// Duplikate
// ─────────────────────────────────────────────────────────────────────────────

/** Schlüssel einer Gruppe für die Route: die Ids aufsteigend, mit Bindestrich. */
export const gruppenSchluessel = (ids: number[]): string => [...ids].sort((a, b) => a - b).join('-');
export const gruppenIds = (schluessel: string): number[] =>
  schluessel.split('-').map(Number).filter(n => Number.isFinite(n) && n > 0);

/** Felder, die beim Zusammenführen von Events verglichen und gewählt werden (wie im Web). */
export const EVENT_ZUSAMMENFUEHREN_FELDER: { key: keyof CyclingEvent; label: string }[] = [
  { key: 'title', label: 'Titel' },
  { key: 'eventDate', label: 'Datum' },
  { key: 'bundesland', label: '(Bundes)Land' },
  { key: 'postalCode', label: 'PLZ' },
  { key: 'city', label: 'Ort' },
  { key: 'locationName', label: 'Treffpunkt' },
  { key: 'street', label: 'Straße' },
  { key: 'houseNumber', label: 'Hausnummer' },
  { key: 'startTime', label: 'Startzeit' },
  { key: 'startTimeEnd', label: 'Startzeit bis' },
  { key: 'category', label: 'Kategorie' },
  { key: 'eventType', label: 'Eventart' },
  { key: 'organizer', label: 'Veranstalter' },
  { key: 'organizerUrl', label: 'Veranstaltungs-Website' },
  { key: 'flyerUrl', label: 'Flyer-Link' },
  { key: 'description', label: 'Beschreibung' },
];

/** Anzeigewert eines Event-Feldes im Vergleich. */
export function eventFeldText(key: keyof CyclingEvent, wert: unknown): string {
  if (wert == null || wert === '') return '';
  if (key === 'eventDate') return formatDayShort(wert as string, '');
  if (key === 'category') return CATEGORY_LABELS[wert as string] ?? String(wert);
  if (key === 'eventType') return EVENT_TYPE_LABELS[wert as string] ?? String(wert);
  if (key === 'bundesland') return displayBundesland(wert as string) ?? String(wert);
  if (typeof wert === 'boolean') return wert ? 'Ja' : 'Nein';
  return String(wert);
}

/** Ausgangswerte fürs Ergebnis: die des Events, das bleibt. Flyer als externer Link (wie im Web). */
export function eventFormWerte(ev: CyclingEvent): Record<string, string> {
  const w: Record<string, string> = {};
  EVENT_ZUSAMMENFUEHREN_FELDER.forEach(({ key }) => { w[key as string] = ev[key] == null ? '' : String(ev[key]); });
  w.flyerUrl = ev.flyerUrlExternal ?? '';
  return w;
}

/** Bei einer Serie das älteste Event behalten (es trägt Adresse und Ranking), sonst das erste. */
export const vorgeschlagenesPrimaer = (gruppe: DuplicateGroup): number => {
  if (gruppe.reason === 'RECURRING_SERIES') {
    return [...gruppe.events].sort((a, b) => a.eventDate.localeCompare(b.eventDate))[0].id;
  }
  return gruppe.events[0].id;
};

export const VERANSTALTER_DUPLIKAT_FELDER: { key: keyof Organizer; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'city', label: 'Ort' },
  { key: 'bundesland', label: '(Bundes)Land' },
  { key: 'address', label: 'Adresse' },
  { key: 'email', label: 'E-Mail' },
  { key: 'phone', label: 'Telefon' },
  { key: 'website', label: 'Website' },
  { key: 'radNetUrl', label: 'rad-net' },
  { key: 'bdrClubNr', label: 'BDR-Club-Nr.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Vorschlag vor dem Genehmigen anpassen (Overrides). Dieselben Regeln wie `initOverrides` im Web
// (ReviewEdits.tsx) und `EventEditService.mergeOverrides` im Backend.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vorbelegung der Anpassung. Felder, die ein Vorschlag oft gar nicht trägt (Quellen, Enricher,
 * ältere Formulare), fallen auf das Event zurück: sonst sähe die Freigabe wie ein Löschen aus.
 * Bei einer Änderung stehen die Links des Events mit drin (vorgeschlagene haben Vorrang).
 */
export function overridesAus(edit: EventEditResponse): ReviewEditOverrides {
  const orig = edit.originalEvent;
  const vorgeschlagen = edit.links ?? [];
  const urls = new Set(vorgeschlagen.map(l => canonicalLinkUrl(l.url)));
  return {
    title: edit.title,
    description: edit.description,
    locationName: edit.locationName,
    street: edit.street,
    houseNumber: edit.houseNumber,
    postalCode: edit.postalCode,
    city: edit.city,
    bundesland: edit.bundesland,
    eventDate: edit.eventDate,
    endDate: edit.endDate ?? orig?.endDate,
    startTime: edit.startTime,
    startTimeEnd: edit.startTimeEnd ?? orig?.startTimeEnd,
    participants: edit.participants ?? orig?.participants,
    organizer: edit.organizer,
    organizerId: edit.organizerId,
    category: edit.category,
    secondaryCategories: edit.secondaryCategories?.length ? edit.secondaryCategories : (orig?.secondaryCategories ?? []),
    eventType: edit.eventType ?? orig?.eventType,
    organizerUrl: edit.organizerUrl,
    // flyerUrlExternal, nicht flyerUrl: sonst wanderte unsere eigene Flyer-Route als „externe URL“ ins Event.
    flyerUrl: edit.flyerUrl ?? orig?.flyerUrlExternal,
    registrationStart: edit.registrationStart ?? orig?.registrationStart,
    registrationMode: edit.registrationMode && edit.registrationMode !== 'UNKNOWN' ? edit.registrationMode : (orig?.registrationMode ?? 'UNKNOWN'),
    registrationState: edit.registrationState && edit.registrationState !== 'UNKNOWN' ? edit.registrationState : (orig?.registrationState ?? 'UNKNOWN'),
    priceTiers: edit.priceTiers ?? orig?.priceTiers ?? [],
    fullyBooked: edit.fullyBooked,
    cancelled: edit.cancelled,
    tentative: edit.tentative,
    permanent: edit.permanent ?? orig?.permanent ?? false,
    seasonFrom: edit.permanent != null ? (edit.seasonFrom ?? null) : (orig?.seasonFrom ?? null),
    seasonUntil: edit.permanent != null ? (edit.seasonUntil ?? null) : (orig?.seasonUntil ?? null),
    areaName: edit.permanent != null ? (edit.areaName ?? null) : (orig?.areaName ?? null),
    distances: edit.distances.map(d => ({
      distanceKm: Number(d.distanceKm),
      elevationMeters: d.elevationMeters,
      routeUrl: d.routeUrl,
      label: d.label,
      durationMinutes: d.durationMinutes,
      eventType: d.eventType ?? null,
      startTime: d.startTime ?? null,
    })),
    links: [...vorgeschlagen, ...(orig?.links ?? []).filter(l => !urls.has(canonicalLinkUrl(l.url)))],
  };
}

/** Overrides → Formularstand des Event-Formulars der App (Bild-Felder bleiben leer, sie zählen hier nicht). */
export function formAusOverrides(o: ReviewEditOverrides): EventFormWerte {
  const permanent = !!o.permanent;
  return {
    title: o.title ?? '',
    description: o.description ?? '',
    locationName: o.locationName ?? '',
    street: o.street ?? '',
    houseNumber: o.houseNumber ?? '',
    postalCode: o.postalCode ?? '',
    city: o.city ?? '',
    bundesland: o.bundesland ?? '',
    eventDate: permanent ? '' : o.eventDate ?? '',
    endDate: permanent ? '' : o.endDate ?? '',
    startTime: o.startTime ?? '',
    startTimeEnd: o.startTimeEnd ?? '',
    participants: o.participants ? String(o.participants) : '',
    organizer: o.organizerId ? '' : o.organizer ?? '',
    organizerId: o.organizerId ?? null,
    organizerName: o.organizerId ? o.organizer ?? '' : '',
    category: o.category,
    secondaryCategories: (o.secondaryCategories ?? []).filter(c => c !== o.category),
    eventType: o.eventType ?? '',
    organizerUrl: o.organizerUrl ?? '',
    flyerUrl: o.flyerUrl ?? '',
    imageUrl: '', imageCredit: '', imageAiGenerated: false, imageFocusX: 50, imageFocusY: 50,
    fullyBooked: !!o.fullyBooked,
    cancelled: !!o.cancelled,
    tentative: !!o.tentative,
    permanent,
    seasonFrom: o.seasonFrom ?? null,
    seasonUntil: o.seasonUntil ?? null,
    areaName: o.areaName ?? '',
    registrationStart: o.registrationStart ?? '',
    registrationMode: o.registrationMode ?? 'UNKNOWN',
    registrationState: o.registrationState ?? 'UNKNOWN',
    active: true,
    distances: o.distances.length ? o.distances.map(d => ({ ...d, routeUrl: d.routeUrl ?? '', label: d.label ?? '' })) : [leereStrecke()],
    komootCollectionUrl: '',
    links: o.links ?? [],
    priceTiers: (o.priceTiers ?? []).map(t => ({ label: t.label ?? '', validUntil: t.validUntil })),
  };
}

/**
 * Formularstand → Overrides fürs Genehmigen. Die Lesart des Backends: `participants` 0/leer
 * behält die Zahl des Vorschlags, ein leerer Flyer den des Vorschlags, ein leerer Anmeldestart
 * (`''`) streicht ihn. Links und Preisstufen gehen als ganze Liste.
 */
export function overridesAusForm(form: EventFormWerte): ReviewEditOverrides {
  const strecken = form.distances.filter(d => d.distanceKm > 0 || (d.durationMinutes ?? 0) > 0);
  return {
    title: form.title.trim(),
    description: form.description || undefined,
    locationName: form.locationName || undefined,
    street: form.street || undefined,
    houseNumber: form.houseNumber || undefined,
    postalCode: form.postalCode,
    city: form.city,
    bundesland: form.bundesland,
    eventDate: form.eventDate,
    endDate: form.endDate || undefined,
    startTime: form.startTime || undefined,
    startTimeEnd: form.startTimeEnd || undefined,
    participants: form.participants ? parseInt(form.participants, 10) || undefined : undefined,
    organizer: form.organizerId ? (form.organizerName || undefined) : (form.organizer || undefined),
    organizerId: form.organizerId ?? undefined,
    category: form.category as ReviewEditOverrides['category'],
    secondaryCategories: form.secondaryCategories,
    eventType: form.eventType || undefined,
    organizerUrl: form.organizerUrl || undefined,
    flyerUrl: form.flyerUrl || undefined,
    fullyBooked: form.fullyBooked,
    cancelled: form.cancelled,
    tentative: form.permanent ? false : form.tentative,
    permanent: form.permanent,
    seasonFrom: form.permanent ? form.seasonFrom : null,
    seasonUntil: form.permanent ? form.seasonUntil : null,
    areaName: form.permanent ? (form.areaName.trim() || null) : null,
    registrationStart: form.registrationStart,
    registrationMode: form.registrationMode,
    registrationState: form.registrationState,
    distances: strecken.map(d => ({
      distanceKm: d.distanceKm,
      elevationMeters: d.elevationMeters,
      routeUrl: d.routeUrl || undefined,
      label: d.label || undefined,
      durationMinutes: d.durationMinutes && d.durationMinutes > 0 ? d.durationMinutes : undefined,
      eventType: d.eventType ?? null,
      startTime: d.startTime || null,
    })),
    priceTiers: form.priceTiers.filter(r => r.validUntil).map(r => ({ label: r.label.trim() || null, validUntil: r.validUntil })),
    links: bereinigteLinks(form.links),
  };
}
